import { JSX, useState } from "react";
import { Col, Divider, Radio, Row } from "antd";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Text from "../../../components/Text/Text";
import Button from "../../../components/Button/Button";
import color from "../../../resource/color";

const StartFromRadioGroup = styled(Radio.Group)<{ $hasError?: boolean }>`
  display: flex;
  width: 100%;
  gap: 8px;
  margin: 12px 0;

  .ant-radio-wrapper {
    display: flex;
    align-items: flex-start;
    flex: 1;
    margin-inline-end: 0;
    padding: 16px 20px;
    border: 1px solid ${(props) => (props.$hasError ? color.error : "#e5e7eb")};
    border-radius: 8px;
    background: #ffffff;
    transition: all 0.2s;
  }
  .ant-radio {
    align-self: flex-start;
    margin-top: 2px;
  }
  .ant-radio + span {
    flex: 1;
    padding-inline-start: 8px;
  }
  .ant-radio-wrapper:hover {
    border-color: #1d4ed8;
  }
  .ant-radio-wrapper-checked {
    border-color: #1d4ed8;
    background-color: #eff6ff;
  }
`;

export interface YearOption {
  label: string;
  value: string;
  year: number;
}

export interface StartFromOption {
  label: string;
  description: string;
  value: string;
}

interface Props {
  year: number;
  setYear: (value: number) => void;
  isAvailableYearLoading : boolean;
  yearsToRender: YearOption[];
  startFrom: string;
  setStartFrom: (value: string) => void;
  setTierProgramId: (value: string) => void;
  startFromOptions: StartFromOption[];
  onNext: () => void;
}

function Step1({
  year,
  setYear,
  yearsToRender,
  isAvailableYearLoading,
  startFrom,
  setStartFrom,
  startFromOptions,
  setTierProgramId,
  onNext,
}: Props): JSX.Element {
  const navigate = useNavigate();

  // Errors only appear after the user presses "ถัดไป"; the button itself stays enabled.
  const [showErrors, setShowErrors] = useState(false);
  const startFromError = showErrors && !startFrom;
  // "สร้างใหม่ทั้งหมด" has no source year to pick; only copying needs one.
  const yearError = showErrors && startFrom === "copy" && !year;

  const handleNext = () => {
    if (!startFrom || (startFrom === "copy" && !year)) {
      setShowErrors(true);
      return;
    }
    onNext();
  };

  if(isAvailableYearLoading){
    return <div>Loading...</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", marginTop: 24 }}>
      <Text fontSize={16} fontWeight={600} color="Text2">
        รูปแบบการสร้างสิทธิประโยชน์
      </Text>
      <Text fontFamily="Sarabun" fontSize={14} fontWeight={400} color="Text3">
        เลือกรูปแบบที่ต้องการ แล้วแก้ไขรายละเอียดในขั้นถัดไป
      </Text>
      <StartFromRadioGroup
        value={startFrom}
        onChange={(e) => setStartFrom(e.target.value)}
        $hasError={startFromError}
      >
        {startFromOptions.map((opt) => (
          <Radio key={opt.value} value={opt.value}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Text
                fontSize={16}
                fontWeight={startFrom === opt.value ? 700 : 500}
                color={startFrom === opt.value ? "primary" : "Text2"}
              >
                {opt.label}
              </Text>
              <Text fontFamily="Sarabun" fontSize={14} fontWeight={400} color="Text3">
                {opt.description}
              </Text>
            </div>
          </Radio>
        ))}
      </StartFromRadioGroup>
      {startFromError && (
        <Text fontFamily="Sarabun" fontSize={14} color="error">
          กรุณาเลือกรูปแบบการสร้างสิทธิประโยชน์
        </Text>
      )}
      {startFrom === "copy" && 
      <>
      <Text fontFamily="Sarabun" fontSize={14} fontWeight={400} color="Text3">
        เลือกปีต้นแบบที่ต้องการคัดลอกสิทธิประโยชน์
      </Text>
      <div style={{ display: "flex", flexDirection: "row", gap: 8, margin: "24px 0" }}>
        {(yearsToRender ?? []).map((y) => (
          <div
            key={y.year}
            onClick={() => {
              setYear(y.year)
              setTierProgramId(y.value)
            }}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: `1px solid ${year === y.year ? "#1D4ED8" : yearError ? color.error : "#E5E7EB"}`,
              cursor: "pointer",
              backgroundColor: year === y.year ? "#EFF6FF" : "#FFFFFF",
            }}
          >
            <Text
              fontSize={14}
              fontWeight={year === y.year ? 700 : 500}
              color={year === y.year ? "primary" : "Text2"}
            >
              {y.label}
            </Text>
          </div>
        ))}
      </div>
      {yearError && (
        <div style={{ marginTop: -16, marginBottom: 16 }}>
          <Text fontFamily="Sarabun" fontSize={14} color="error">
            กรุณาเลือกปีต้นแบบที่ต้องการคัดลอก
          </Text>
        </div>
      )}
      </>}
      <Divider />
      <Row justify="start" gutter={8}>
        <Col>
          <Button
            typeButton="border-primary"
            title="ยกเลิก"
            style={{ width: "auto" }}
            onClick={() => navigate(-1)}
          />
        </Col>
        <Col>
          <Button
            typeButton="primary"
            title="ถัดไป"
            style={{ width: "auto" }}
            onClick={handleNext}
          />
        </Col>
      </Row>
    </div>
  );
}

export default Step1;
